import { useCallback, useEffect, useState } from "react";
import { useImpersonation } from "~/hooks/useImpersonation";

function formatTimeRemaining(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function CountdownTimer({
  expiresAt,
  onExpire,
}: {
  expiresAt: string;
  onExpire: () => void;
}) {
  const [secondsLeft, setSecondsLeft] = useState(() => {
    const diff = Math.floor(
      (new Date(expiresAt).getTime() - Date.now()) / 1000,
    );
    return Math.max(0, diff);
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const diff = Math.floor(
        (new Date(expiresAt).getTime() - Date.now()) / 1000,
      );
      const remaining = Math.max(0, diff);
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        onExpire();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, onExpire]);

  return (
    <span className="font-mono text-xs">
      {formatTimeRemaining(secondsLeft)}
    </span>
  );
}

/**
 * Fixed bottom-center pill showing impersonation status.
 * Small and unobtrusive but always visible.
 */
const ImpersonationBanner = () => {
  const { impersonationState, endImpersonation } = useImpersonation();
  const [isExiting, setIsExiting] = useState(false);

  const handleEnd = useCallback(() => {
    setIsExiting(true);
    void endImpersonation();
  }, [endImpersonation]);

  if (!impersonationState.isActive) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-amber-500 text-indigo-text-on-accent
        rounded-full px-4 py-2 flex items-center gap-3 shadow-lg"
      style={{ zIndex: "var(--indigo-z-custom-toast)" }}
    >
      <span className="text-xs font-medium whitespace-nowrap">
        Impersonating <strong>{impersonationState.targetUsername}</strong>
      </span>
      <span className="text-white/60">|</span>
      {impersonationState.expiresAt && (
        <CountdownTimer
          expiresAt={impersonationState.expiresAt}
          onExpire={handleEnd}
        />
      )}
      <button
        onClick={handleEnd}
        disabled={isExiting}
        className="bg-indigo-canvas-highlight text-amber-700 px-3 py-0.5 rounded-full text-xs font-medium
          hover:bg-amber-50 transition-colors whitespace-nowrap disabled:opacity-50"
      >
        {isExiting ? "Exiting..." : "Exit"}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
